import { AxiosInstance, AxiosResponse } from 'axios';
import {
  AutoChargeDto,
  BillingStripeInvoicesListDto,
  PauseSubscriptionOptionsDto,
  PayAllLinkDto,
  ReferralLinkDto,
  StripeCustomerDto,
  StripePaymentMethodDto,
  StripePaymentMethodsListDto,
  TaxIdDto,
  TenantNotificationSettingsDto,
} from './dto';
import {
  Customer,
  Invoice,
  PauseSubscriptionOption,
  ReferralLink,
  toCustomer,
  toInvoices,
  toPauseSubscriptionOptions,
} from './types';

type BillingPortalSessionDto = {
  url: string;
};

type ChangeBillingPlanDto = {
  newBillingPlanName: string;
};

type CancelBillingPlanDto = {
  reasons: string[];
  comment: string;
};

type ReactivateBillingPlanDto = {
  termsAndConditionsAccepted: boolean;
  billingIsRecurringAndImmediateAcknowledged: boolean;
  offer: string | null;
  billingPlan: string | null;
};

export type BillingApi = ReturnType<typeof getBillingApi>;

export const getBillingApi = (axiosInstance: AxiosInstance) => {
  const createBillingPortalSession = async (): Promise<BillingPortalSessionDto> =>
    axiosInstance.post<BillingPortalSessionDto>('/billing/create-session').then((r) => r.data);

  const finalizeInvoices = async (): Promise<Invoice[]> =>
    axiosInstance
      .post<BillingStripeInvoicesListDto>('/billing/invoices/finalize')
      .then((r) => toInvoices(r.data));

  const getPaymentMethods = async (): Promise<StripePaymentMethodDto[]> =>
    axiosInstance
      .get<StripePaymentMethodsListDto>('/billing/payment-methods')
      .then((r) => r.data.paymentMethods);

  const getBillingCustomer = async (): Promise<Customer> =>
    axiosInstance.get<StripeCustomerDto>('/billing/customer').then((r) => toCustomer(r.data));

  const changeBillingPlan = async (newBillingPlanName: string) => {
    const body: ChangeBillingPlanDto = {
      newBillingPlanName,
    };

    await axiosInstance.post('/billing/plan/change', body);
  };

  const cancelBillingPlan = async (request: CancelBillingPlanDto): Promise<Customer> =>
    axiosInstance
      .post<CancelBillingPlanDto, AxiosResponse<StripeCustomerDto>>('/billing/plan/cancel', request)
      .then((r) => toCustomer(r.data));

  const reactivateBillingPlan = async (request: ReactivateBillingPlanDto): Promise<Customer> =>
    axiosInstance
      .post<CancelBillingPlanDto, AxiosResponse<StripeCustomerDto>>(
        '/billing/plan/reactivate',
        request
      )
      .then((r) => toCustomer(r.data));

  const getTenantNotificationSettings = async (): Promise<TenantNotificationSettingsDto> =>
    axiosInstance
      .get<TenantNotificationSettingsDto>('/tenants/current/notification-settings')
      .then((r) => r.data);

  const updateTenantNotificationSettings = async (
    notificationSettings: TenantNotificationSettingsDto
  ): Promise<TenantNotificationSettingsDto> =>
    axiosInstance
      .put<TenantNotificationSettingsDto>(
        '/tenants/current/notification-settings',
        notificationSettings
      )
      .then((r) => r.data);

  const setTaxId = async (taxId: TaxIdDto): Promise<TaxIdDto> =>
    axiosInstance.post<TaxIdDto>('/billing/customer/tax-id', taxId).then((r) => r.data);

  const getPayAllLink = async (): Promise<PayAllLinkDto> =>
    axiosInstance.get<PayAllLinkDto>('/billing/pay-all-link').then((r) => r.data);

  const createReferralLink = async (): Promise<ReferralLink> =>
    axiosInstance.post<ReferralLinkDto>('/billing/referral-link').then((r) => r.data);

  const canRevertCanceledSubscription = async (): Promise<{
    canRevert: boolean;
    reason?: string;
  }> =>
    axiosInstance
      .get<{ canRevert: boolean; reason?: string }>('/billing/can-revert-cancellation')
      .then((r) => r.data);

  const revertCancellation = async (): Promise<Customer> =>
    axiosInstance
      .post<undefined, AxiosResponse<StripeCustomerDto>>('/billing/revert-cancellation')
      .then((r) => toCustomer(r.data));

  const getPauseSubscriptionOptions = async (): Promise<PauseSubscriptionOption[]> =>
    axiosInstance
      .get<PauseSubscriptionOptionsDto>('/billing/plan/pause')
      .then((r) => toPauseSubscriptionOptions(r.data));

  const pauseBillingPlan = async (monthsToPause: number): Promise<Customer> =>
    axiosInstance
      .post<undefined, AxiosResponse<StripeCustomerDto>>('/billing/plan/pause', { monthsToPause })
      .then((r) => toCustomer(r.data));

  const unpauseBillingPlan = async (): Promise<void> =>
    axiosInstance.post<undefined, AxiosResponse<void>>('/billing/plan/unpause').then(() => {});

  const setAutoCharge = async (request: AutoChargeDto): Promise<Customer> =>
    axiosInstance
      .post<AutoChargeDto, AxiosResponse<StripeCustomerDto>>(
        '/billing/customer/auto-charge',
        request
      )
      .then((r) => toCustomer(r.data));

  return {
    createBillingPortalSession,
    getBillingCustomer,
    finalizeInvoices,
    getPaymentMethods,
    changeBillingPlan,
    reactivateBillingPlan,
    cancelBillingPlan,
    getTenantNotificationSettings,
    updateTenantNotificationSettings,
    setTaxId,
    getPayAllLink,
    createReferralLink,
    canRevertCanceledSubscription,
    revertCancellation,
    getPauseSubscriptionOptions,
    pauseBillingPlan,
    unpauseBillingPlan,
    setAutoCharge,
  };
};
